import {
  APIActions,
  Box,
  CustomDialog,
  MUIDataTable,
  MUIDataTableColumnDef,
  Pill,
  Typography,
  useSnackbarHandler,
} from 'web-library';
import React, { useEffect, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { Theme as AugmentedTheme } from '@mui/material';

import { makeStyles } from 'tss-react/mui';

import DeleteButton from 'web-library/components/buttons/DeleteButton';
import FormHeader from '../../../components/form-items/FormHeader';
import { AdminDeletedSubjectDetails } from './model';
import EditButton from '../../../components/buttons/EditButton';
import {
  ExamSubjects,
  SubjectType,
  UpdateSubjectRegistrationDraftRequest,
  ViewSelectedSubjectPapers,
} from '../../../api/candidate/exam/model';
import { EditPaperSubjectModal } from '../../../components/PaperSubjectModal';
import { useGetUniqueLinkSubjectDetails } from '../../../api-refactored/unique-link/useGetUniqueLinkSubjectDetails';
import { displaySubjectType } from './AdminSubjectListing';

import {typographyTheme} from "web-library/theme/helper/tssHelper"
const useStyles = makeStyles()((theme) =>
({
  formHeaderContainer: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  optionalPapers: {
     ...typographyTheme(theme, 'subtitle2'),
    color: theme.color.text.textSecondary,
  },
  subjectType: {
    color: theme.color.text.textTertiary,
    fontSize: '0.875rem',
    fontWeight: 'bold',
  },
  pill: {
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(0.4),
    },
  },
  dataTable: {
    marginBottom: theme.spacing(3),
  },
  buttonContainer: {
    display: 'flex',
    columnGap: theme.spacing(3),
    alignItems: 'space-evenly',
  },
}));

interface TableColumnsProps {
  adminSelectedSubjects: any;
  setAdminEditedSubjectDetails: React.Dispatch<React.SetStateAction<any>>;
  setAdminDeletedSubjectDetails: React.Dispatch<
    React.SetStateAction<{
      subjectName: string;
      subjectCode: string;
    }>
  >;
  setSelectedSubjectCodeForChoosePapers: React.Dispatch<React.SetStateAction<string>>;
  setOpenDeleteModal: React.Dispatch<React.SetStateAction<boolean>>;
  t: TFunction<'translation'>;
  classes: Record<string,string>;
  disabled: boolean;
}

const tableColumns = ({
  adminSelectedSubjects,
  setAdminEditedSubjectDetails,
  setAdminDeletedSubjectDetails,
  setSelectedSubjectCodeForChoosePapers,
  setOpenDeleteModal,
  t,
  classes,
  disabled,
}: TableColumnsProps): MUIDataTableColumnDef[] => [
    {
      name: 'subjectName',
      label: t('exam->>SelectSubjects.SubjectName'),
      options: {
        sort: false,
        customBodyRenderLite: (dataIndex: number) => {
          const optionalPaperList: string[] = [];
          adminSelectedSubjects[dataIndex].optionalPapers?.forEach(
            (item: ViewSelectedSubjectPapers) => {
              if (item.selected) {
                optionalPaperList.push(`${t('exam->>ViewSelectedSubjects.Paper')} ${item.paperNo}`);
              }
            }
          );

          return (
            <Box className={classes.subjectContainer}>
              <Typography className={classes.subjectText} variant="body2">
                {adminSelectedSubjects[dataIndex].subjectName} (
                {adminSelectedSubjects[dataIndex].subjectCode})
              </Typography>
              {optionalPaperList.length ? (
                <Typography variant="body2" className={classes.optionalPapers}>
                  {t('exam->>ViewSelectedSubjects.OptionalPapers')}: {optionalPaperList.join(', ')}
                </Typography>
              ) : null}
              <Typography className={classes.subjectType}>
                {displaySubjectType(adminSelectedSubjects[dataIndex].subjectType as SubjectType)}
              </Typography>
            </Box>
          );
        },
      },
    },
    {
      name: 'subjectLevel',
      label: t('exam->>SelectSubjects.SubjectLevel'),
      options: {
        sort: false,
        setCellProps: () => ({ style: { width: '10%' } }),
      },
    },
    {
      name: 'examSeries',
      label: t('exam->>SelectSubjects.ExamSeries'),
      options: {
        sort: false,
      },
    },
    {
      name: 'examLevel',
      label: t('exam->>SelectSubjects.ExamLevel'),
      options: {
        sort: false,
      },
    },
    {
      name: 'language',
      label: t('exam->>SelectSubjects.LanguageMedium'),
      options: {
        sort: false,
      },
    },
    {
      name: 'action',
      label: t('admin->>UserGroups.Action'),
      options: {
        sort: false,
        customBodyRenderLite: (dataIndex: number) => {
          const checkEditColumn = adminSelectedSubjects[dataIndex].actionRequired;

          return (
            <Box className={classes.buttonContainer}>
              {checkEditColumn && !disabled && (
                <EditButton
                  buttonId={dataIndex}
                  label={t('exam->>ViewSelectedSubjects.Edit')}
                  editHandler={() => {
                    setAdminEditedSubjectDetails(
                      JSON.parse(JSON.stringify(adminSelectedSubjects[dataIndex]))
                    );
                    setSelectedSubjectCodeForChoosePapers(
                      adminSelectedSubjects[dataIndex].subjectCode
                    );
                  }}
                />
              )}
              <DeleteButton
                deleteHandler={() => {
                  setAdminDeletedSubjectDetails({
                    subjectName: adminSelectedSubjects[dataIndex].subjectName,
                    subjectCode: adminSelectedSubjects[dataIndex].subjectCode,
                  });
                  setOpenDeleteModal(true);
                }}
                disabled={disabled}
                label={t('announcementsNotifications->>Listing.Remove')}
                typographyVariant="h5"
              />
            </Box>
          );
        },
      },
    },
  ];

interface AdminSelectedSubjectsProps {
  adminSelectedSubjects: any;
  setAdminSelectedSubjects: React.Dispatch<React.SetStateAction<any>>;
  adminEditedSubjectDetails: ExamSubjects;
  setAdminEditedSubjectDetails: React.Dispatch<React.SetStateAction<ExamSubjects>>;
  adminDeletedSubjectDetails: AdminDeletedSubjectDetails;
  setAdminDeletedSubjectDetails: React.Dispatch<React.SetStateAction<AdminDeletedSubjectDetails>>;
  disabled?: boolean;
}

const AdminSelectedSubjects = ({
  adminSelectedSubjects,
  setAdminSelectedSubjects,
  adminEditedSubjectDetails,
  setAdminEditedSubjectDetails,
  adminDeletedSubjectDetails,
  setAdminDeletedSubjectDetails,
  disabled = false,
}: AdminSelectedSubjectsProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { snackbarHandler } = useSnackbarHandler();

  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);

  const handleCloseDeleteModal = () => setOpenDeleteModal(false);

  const handleDeleteSelectedSubject = () => {
    const filteredSelectedSubject = adminSelectedSubjects.filter(
      (selectedSubject: any) =>
        selectedSubject.subjectCode !== adminDeletedSubjectDetails.subjectCode
    );
    setAdminSelectedSubjects(filteredSelectedSubject);
    const deletedSubjectDetails = `${adminDeletedSubjectDetails.subjectName} (${adminDeletedSubjectDetails.subjectCode})`;
    snackbarHandler?.setGlobalSnackbar({
      targetSeverity: 'success',
      targetData: t('exam->>SubjectSnackbar.HasBeenRemoved', {
        subjectDetails: deletedSubjectDetails,
      }),
    });
    handleCloseDeleteModal();
  };

  const [
    selectedSubjectCodeForChoosePapers,
    setSelectedSubjectCodeForChoosePapers,
  ] = useState<string>('');

  const { data: subjectDetails } = useGetUniqueLinkSubjectDetails({
    subjectCode: selectedSubjectCodeForChoosePapers,
  });

  const [paperState, setPaperState] = useState({});
  const paperStateHandling = { paperState, setPaperState };

  useEffect(() => {
    if (subjectDetails) {
      const initialRegisteredSubjectState = {
        applicationUuid: '',
        examRegistrationDetailUuid: '',
        examSeriesUuid: subjectDetails?.examSeries.find(
          (examSeriesValue) => examSeriesValue.description === adminEditedSubjectDetails?.examSeries
        )?.code,
        optionalPapers: adminEditedSubjectDetails?.optionalPapers,
        callbackVariable: {
          subjectCode: subjectDetails?.subjectCode,
          subjectName: subjectDetails?.subjectName,
          subjectLevel: subjectDetails?.subjectLevel,
        },
      };
      setPaperState((prevState: UpdateSubjectRegistrationDraftRequest) => ({
        ...prevState,
        ...initialRegisteredSubjectState,
      }));
    }
  }, [
    subjectDetails,
    adminEditedSubjectDetails?.examSeries,
    adminEditedSubjectDetails?.optionalPapers,
  ]);

  return (
    <>
      <Box className={classes.formHeaderContainer}>
        <FormHeader title={t('exam->>ViewSelectedSubjects.SelectedSubjects')} />
        <Pill className={classes.pill} variant="info" label={adminSelectedSubjects.length} />
      </Box>
      <Box className={classes.dataTable}>
        <MUIDataTable
          columns={tableColumns({
            adminSelectedSubjects,
            setAdminEditedSubjectDetails,
            setAdminDeletedSubjectDetails,
            setSelectedSubjectCodeForChoosePapers,
            setOpenDeleteModal,
            t,
            classes,
            disabled,
          })}
          data={adminSelectedSubjects}
          emptyDataLabel={t('exam->>ViewSelectedSubjects.EmptyDataTableContentAdmin')}
          options={{
            sort: true,
            selectToolbarPlacement: 'none',
            selectableRows: 'none',
          }}
        />
      </Box>
      {adminSelectedSubjects && subjectDetails && adminEditedSubjectDetails && (
        <EditPaperSubjectModal
          mutateSubjectHandler={{
            mutateSubjectStatus: 'idle',
          }}
          paperStateHandling={paperStateHandling}
          registeredSubjectHandling={{
            registeredSubject: {
              ...subjectDetails,
              optionalPapers: subjectDetails?.optionalPapers.map((optionalPaper, index) => ({
                ...optionalPaper,
                selected: adminSelectedSubjects.find(
                  (adminSelectedSubject: any) =>
                    adminSelectedSubject.subjectCode === subjectDetails.subjectCode
                )?.optionalPapers[index].selected,
              })),
            },
            registeredSubjectStatus: 'success',
          }}
          applicationUuid=""
          onClose={() => setSelectedSubjectCodeForChoosePapers('')}
          modelOpen={!!selectedSubjectCodeForChoosePapers}
          mutateChoosePaperSubject={(subjectData) => {
            const selectedExamSeries = subjectDetails?.examSeries.find(
              (examSeriesValue) => examSeriesValue.code === subjectData.examSeriesUuid
            );
            setAdminSelectedSubjects((existingAdminSelectedSubjects: any) =>
              existingAdminSelectedSubjects.map((subject: any) =>
                subject.subjectCode === subjectDetails?.subjectCode
                  ? {
                    ...subject,
                    examSeries: selectedExamSeries?.description,
                    examSeriesUuid: subjectData?.examSeriesUuid,
                    optionalPapers: subjectData.optionalPapers,
                  }
                  : subject
              )
            );
            setSelectedSubjectCodeForChoosePapers('');
            const editedSubjectDetails = `${adminEditedSubjectDetails?.subjectName} (${adminEditedSubjectDetails?.subjectCode})`;
            snackbarHandler?.setGlobalSnackbar({
              targetSeverity: 'success',
              targetData: t('exam->>SubjectSnackbar.HasBeenUpdated', {
                subjectDetails: editedSubjectDetails,
              }),
            });
          }}
        />
      )}
      <CustomDialog
        open={openDeleteModal}
        title={t('exam->>ViewSelectedSubjects.RemoveFromSelectedSubjects', {
          subjectNameSubjectCode: `${adminDeletedSubjectDetails.subjectName} (${adminDeletedSubjectDetails.subjectCode})`,
        })}
        message={t('exam->>ViewSelectedSubjects.RemoveFromSelectedSubjectsQuestion')}
        action={
          <APIActions
            onClose={handleCloseDeleteModal}
            apiCall={handleDeleteSelectedSubject}
            cancelString={t('exam->>ViewSelectedSubjects.Cancel')}
            apiString={t('exam->>ViewSelectedSubjects.Proceed')}
          />
        }
      />
    </>
  );
};

export default AdminSelectedSubjects;
